import { useEffect, useState, useMemo } from "react";
import { Loader2, BarChart3, ChevronDown } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Monthly sales card — Xero P&L revenue per month, with a date-range picker
// (from / to as YYYY-MM inputs) and a cash / accrual basis toggle. The
// original bar-grid version cramped the dollar labels on mobile; this
// rewrite uses a scrollable horizontal row of "month tiles" that each show
// their number in full without truncation.
const NZD_FULL = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    maximumFractionDigits: 2,
});
const NZD_COMPACT = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    maximumFractionDigits: 0,
});

function toMonthInput(iso) {
    // "YYYY-MM-DD" -> "YYYY-MM" for <input type="month">.
    return (iso || "").slice(0, 7);
}
function firstDayOfMonth(ymd) {
    return `${ymd}-01`;
}
function lastDayOfMonth(ymd) {
    // ymd = "YYYY-MM"
    const [y, m] = ymd.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return `${ymd}-${String(last).padStart(2, "0")}`;
}

const PRESETS = [
    { key: "ytd", label: "This year" },
    { key: "last12", label: "Last 12 months" },
    { key: "lastyear", label: "Last year" },
    { key: "jan-jul", label: "Jan – Jul" },
    { key: "custom", label: "Custom…" },
];

function presetToRange(key) {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    switch (key) {
        case "ytd":
            return { from: `${y}-01`, to: `${y}-${String(m).padStart(2, "0")}` };
        case "last12": {
            const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
            return {
                from: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`,
                to: `${y}-${String(m).padStart(2, "0")}`,
            };
        }
        case "lastyear":
            return { from: `${y - 1}-01`, to: `${y - 1}-12` };
        case "jan-jul":
            return { from: `${y}-01`, to: `${y}-07` };
        default:
            return null;
    }
}

export default function MonthlySalesCard() {
    const { activeClientId, activeClient } = useAuth();
    const isSoleTrader = activeClient?.entity_type === "sole_trader";
    const [state, setState] = useState({ loading: true, months: [], grand_total: 0, source: "none", from: "", to: "" });
    const [preset, setPreset] = useState("ytd");
    const [basis, setBasis] = useState("accrual");
    const initialRange = useMemo(() => presetToRange("ytd"), []);
    const [range, setRange] = useState(initialRange);

    // Whenever preset changes to a non-custom option, snap the range.
    useEffect(() => {
        if (preset === "custom") return;
        const r = presetToRange(preset);
        if (r) setRange(r);
    }, [preset]);

    // Fetch on any change of range, basis or active client.
    useEffect(() => {
        if (!range?.from || !range?.to) return;
        setState((s) => ({ ...s, loading: true }));
        api
            .get("/portal/reports/monthly-sales", {
                params: {
                    from: firstDayOfMonth(range.from),
                    to: lastDayOfMonth(range.to),
                    basis,
                    ...(activeClientId ? { client_id: activeClientId } : {}),
                },
            })
            .then(({ data }) => setState({
                loading: false,
                months: data.months || [],
                grand_total: data.grand_total || 0,
                source: data.source || "none",
                from: data.from || "",
                to: data.to || "",
            }))
            .catch(() => setState({ loading: false, months: [], grand_total: 0, source: "error", from: "", to: "" }));
    }, [range?.from, range?.to, basis, activeClientId]);

    const hasData = state.source === "xero" && state.grand_total > 0;
    const max = Math.max(1, ...state.months.map((m) => m.total || 0));
    const bestMonth = state.months.reduce(
        (b, m) => ((m.total || 0) > (b?.total || 0) ? m : b),
        null,
    );

    return (
        <div className="rounded-2xl border border-[#E4E4E7] bg-white p-5 sm:p-6" data-testid="monthly-sales-card">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="co-label text-[#0E3B2E] flex items-center gap-1.5">
                        <BarChart3 className="h-3.5 w-3.5" /> Monthly sales
                    </div>
                    <h3 className="mt-1 font-display text-lg sm:text-xl font-semibold tracking-tight">
                        Where were your busy months?
                    </h3>
                </div>
                {hasData && (
                    <div className="text-right shrink-0">
                        <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Total for range</div>
                        <div className="font-display text-xl font-semibold tabular-nums" data-testid="monthly-sales-total">
                            {NZD_COMPACT.format(state.grand_total || 0)}
                        </div>
                    </div>
                )}
            </div>

            {/* Controls: preset + custom dates + basis toggle */}
            <div className="mt-4 flex flex-wrap items-end gap-2 sm:gap-3" data-testid="monthly-sales-controls">
                <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                    Range
                    <div className="relative mt-1">
                        <select
                            value={preset}
                            onChange={(e) => setPreset(e.target.value)}
                            data-testid="monthly-sales-preset"
                            className="appearance-none rounded-md border border-[#E4E4E7] bg-white pl-3 pr-8 py-1.5 text-sm text-[#0F1115] focus:outline-none focus:ring-2 focus:ring-[#0E3B2E]/20"
                        >
                            {PRESETS.map((p) => (
                                <option key={p.key} value={p.key}>{p.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#5E636E]" />
                    </div>
                </label>

                <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                    From
                    <input
                        type="month"
                        value={range?.from || ""}
                        onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }}
                        data-testid="monthly-sales-from"
                        className="mt-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1.5 text-sm text-[#0F1115] focus:outline-none focus:ring-2 focus:ring-[#0E3B2E]/20"
                    />
                </label>
                <label className="flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                    To
                    <input
                        type="month"
                        value={range?.to || ""}
                        onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }}
                        data-testid="monthly-sales-to"
                        className="mt-1 rounded-md border border-[#E4E4E7] bg-white px-2 py-1.5 text-sm text-[#0F1115] focus:outline-none focus:ring-2 focus:ring-[#0E3B2E]/20"
                    />
                </label>

                <div className="ml-auto flex flex-col text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">
                    Basis
                    <div className="mt-1 inline-flex rounded-md border border-[#E4E4E7] bg-white p-0.5 text-[13px]" role="tablist" aria-label="Report basis">
                        {["cash", "accrual"].map((b) => (
                            <button
                                key={b}
                                type="button"
                                role="tab"
                                aria-selected={basis === b}
                                onClick={() => setBasis(b)}
                                data-testid={`monthly-sales-basis-${b}`}
                                className={`px-3 py-1 rounded ${basis === b ? "bg-[#0E3B2E] text-white" : "text-[#0F1115] hover:bg-[#F5F4EF]"}`}
                            >
                                {b === "cash" ? "Cash" : "Accrual"}
                            </button>
                        ))}
                    </div>
                    <div className="mt-1.5 text-[11px] normal-case tracking-normal text-[#5E636E] max-w-[280px]" data-testid="monthly-sales-basis-hint">
                        {basis === "cash"
                            ? "Cash: only when money actually hit the bank."
                            : "Accrual: when the invoice was raised, paid or not."}
                    </div>
                </div>
            </div>

            {/* Chart / empty / loading */}
            {state.loading ? (
                <div className="mt-5 flex items-center gap-2 text-sm text-[#5E636E]" data-testid="monthly-sales-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading your sales…
                </div>
            ) : !hasData ? (
                <p className="mt-5 text-sm text-[#5E636E]" data-testid="monthly-sales-empty">
                    {state.source === "none"
                        ? (isSoleTrader
                            ? "Accept the Prosaic invite (card above) and this fills in with your real bank activity in under a minute."
                            : "Connect Xero (card above) and this fills in with your real sales in under a minute.")
                        : state.source === "error"
                            ? "Couldn't load your sales right now — try changing the range or reload."
                            : (isSoleTrader
                                ? "No activity in this range — try a wider one, or check that Prosaic has this period synced."
                                : "No sales in this range — try a wider one, or check that this period has invoices in Xero.")}
                </p>
            ) : (
                <>
                    {/* Scrollable row of month tiles — each tile fits its own number
                        without truncation. Bar + tile keeps the visual comparison
                        without the previous cramped grid. */}
                    <div className="mt-5 -mx-1 overflow-x-auto" data-testid="monthly-sales-chart">
                        <div className="flex items-end gap-2 px-1 min-w-full">
                            {state.months.map((m) => {
                                const pct = Math.max(4, Math.round(((m.total || 0) / max) * 100));
                                const isBest = bestMonth && m.month === bestMonth.month && m.year === bestMonth.year && (m.total || 0) > 0;
                                return (
                                    <div
                                        key={`${m.year}-${m.month}`}
                                        className="flex flex-col items-center gap-1.5 min-w-[76px] sm:min-w-[92px]"
                                        data-testid={`monthly-sales-bar-${m.year}-${m.month}`}
                                    >
                                        <div className="h-28 sm:h-32 w-full flex items-end">
                                            <div
                                                className={`w-full rounded-t-md transition-all ${isBest ? "bg-[#0E3B2E]" : "bg-[#0E3B2E]/25"}`}
                                                style={{ height: `${pct}%` }}
                                                title={`${m.label}: ${NZD_FULL.format(m.total || 0)}`}
                                            />
                                        </div>
                                        <div className="text-[11px] font-medium text-[#0F1115]">
                                            {m.label}
                                        </div>
                                        <div className="text-[12px] font-medium text-[#0F1115] tabular-nums whitespace-nowrap" data-testid={`monthly-sales-value-${m.year}-${m.month}`}>
                                            {NZD_FULL.format(m.total || 0)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    {bestMonth && (bestMonth.total || 0) > 0 && (
                        <p className="mt-4 text-[13px] text-[#5E636E]" data-testid="monthly-sales-caption">
                            Busiest month in this range: <span className="font-medium text-[#0F1115]">{bestMonth.label}</span> — {NZD_FULL.format(bestMonth.total || 0)}.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
